import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";

import { cn, cnState } from "@beweegbaas/ui/lib/utils";

function TooltipProvider({ delay = 100, ...props }: TooltipPrimitive.Provider.Props) {
  return <TooltipPrimitive.Provider data-slot="tooltip-provider" delay={delay} {...props} />;
}

function Tooltip<Payload>({ ...props }: TooltipPrimitive.Root.Props<Payload>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
}

function TooltipTrigger<Payload>({ ...props }: TooltipPrimitive.Trigger.Props<Payload>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  className,
  side = "top",
  sideOffset = 4,
  align = "center",
  alignOffset = 0,
  collisionPadding,
  collisionBoundary,
  sticky,
  anchor,
  positionerClassName,
  arrow = true,
  arrowClassName,
  children,
  ...props
}: TooltipPrimitive.Popup.Props &
  Pick<
    TooltipPrimitive.Positioner.Props,
    | "align"
    | "alignOffset"
    | "side"
    | "sideOffset"
    | "collisionPadding"
    | "collisionBoundary"
    | "sticky"
    | "anchor"
  > & {
    arrow?: boolean;
    arrowClassName?: string;
    positionerClassName?: string;
  }) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        collisionBoundary={collisionBoundary}
        sticky={sticky}
        anchor={anchor}
        className={cn("isolate z-50", positionerClassName)}
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={cnState(
            `data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95
            data-closed:animate-out
            data-closed:fade-out-0 data-closed:zoom-out-95
            data-[side=bottom]:slide-in-from-top-2
            data-[side=left]:slide-in-from-right-2
            data-[side=right]:slide-in-from-left-2
            data-[side=top]:slide-in-from-bottom-2 bg-foreground text-background z-50
            dark:bg-popover dark:text-popover-foreground dark:ring-border w-fit
            max-w-xs origin-(--transform-origin) rounded-md px-3 py-1.5 text-xs
            dark:ring-1`,
            className,
          )}
          {...props}
        >
          {children}
          {arrow && (
            <TooltipPrimitive.Arrow
              className={cn(
                `squircle bg-foreground fill-foreground z-50 size-2.5
                translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]
                data-[side=bottom]:top-1 data-[side=left]:top-1/2!
                data-[side=left]:-right-1 data-[side=left]:-translate-y-1/2
                data-[side=right]:top-1/2! data-[side=right]:-left-1
                data-[side=right]:-translate-y-1/2 data-[side=top]:-bottom-2.5
                supports-squircle:rounded-full`,
                // Dark: popover fill matching the bubble, with the ring's border color only on
                // the two outward-facing edges of the rotated square, so the arrow reads as part
                // of the outlined bubble. Squircle rounding is dropped — it turns the small
                // rotated square into a blob.
                `dark:bg-popover dark:fill-popover dark:border-border
                dark:supports-squircle:rounded-[2px]`,
                `dark:border-r dark:border-b
                dark:data-[side=bottom]:border-r-0 dark:data-[side=bottom]:border-b-0
                dark:data-[side=bottom]:border-l dark:data-[side=bottom]:border-t
                dark:data-[side=left]:border-b-0 dark:data-[side=left]:border-t
                dark:data-[side=right]:border-r-0 dark:data-[side=right]:border-l`,
                arrowClassName,
              )}
            />
          )}
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipContent, TooltipPrimitive, TooltipProvider, TooltipTrigger };
